import { Dispatch, SetStateAction, useMemo } from 'react';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { EditableIngredient } from '../EditableIngredient';
import { TextButtonWithIcon } from '@/components';
import { generateNumericId, IngredientWithAmount } from '@/core';
import palette from '@/theme/palette.ts';

type FormulaTableProps = {
  ingredientsWithAmount: IngredientWithAmount[];
  setIngredientsWithAmount: Dispatch<SetStateAction<IngredientWithAmount[]>>;
};

export const FormulaTable = ({
  ingredientsWithAmount,
  setIngredientsWithAmount,
}: FormulaTableProps) => {
  const { t } = useTranslation();
  const wasIngredientAdded = useMemo(
    () => ingredientsWithAmount.length > 0,
    [ingredientsWithAmount],
  );

  const handleAddIngredientClick = () => {
    const newIngredient: IngredientWithAmount = {
      ingredient: {
        id: generateNumericId(),
        name: '',
        unit: '',
        description: '',
        price: '0',
        is_active: false,
      },
      amount: 1,
    };
    setIngredientsWithAmount([...ingredientsWithAmount, newIngredient]);
  };

  return (
    <Table size="medium" sx={{ mt: 2, width: '100%' }}>
      {wasIngredientAdded ? (
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '50%' }}>{t('INGREDIENT.INGREDIENTS')}</TableCell>
            <TableCell sx={{ width: '20%' }}>{t('INGREDIENT.GROSS_WEIGHT')}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
      ) : (
        <Stack direction={'column'} ml={8} mt={3}>
          <Typography variant="body1" fontWeight={700}>
            {t('INGREDIENT.FILL_IN_TITLE')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ width: '80%' }}>
            {t('INGREDIENT.FILL_IN_DESCRIPTION')}
          </Typography>
        </Stack>
      )}
      <TableBody>
        {ingredientsWithAmount.map((ingredientWithAmount) => (
          <EditableIngredient
            key={ingredientWithAmount.ingredient.id}
            ingredientWithAmount={ingredientWithAmount}
            ingredientsWithAmount={ingredientsWithAmount}
            setIngredientsWithAmount={setIngredientsWithAmount}
          />
        ))}
        <TableRow>
          <TableCell
            sx={{
              borderBottom: 'none',
              color: palette.primary.main,
              pl: wasIngredientAdded ? 0 : 6,
            }}
          >
            <TextButtonWithIcon
              iconName={'add'}
              label={wasIngredientAdded ? t('GLOBAL.ADD') : t('INGREDIENT.ADD')}
              sx={{ my: wasIngredientAdded ? 0 : 1 }}
              onClick={handleAddIngredientClick}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
